"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card } from "@/components/ui/card";
export default function Standard() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const instruments = [{
    label: "COMPLIANCE — TIER ONE",
    name: "Strategic Tax Planning",
    description: "A flagship advisory service engineered for long-term financial health. Delivers exceptional clarity on tax liabilities, deduction optimization, and future-proofing your assets in a single comprehensive session.",
    specs: [{
      k: "Planning Horizon",
      v: "Annual"
    }, {
      k: "Accuracy",
      v: "100%"
    }, {
      k: "Review Time",
      v: "45min"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/tax_planning_session.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/tax_planning_session.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/tax_planning_session.png-wvc-sizes"
  }, {
    label: "COMPLIANCE — FILING",
    name: "Corporate Tax Compliance",
    description: "Ultra-efficient filing protocols that quantify business obligations with sub-ledger precision. The most reliable indicator of fiscal health, captured in a single, seamless reporting cycle.",
    specs: [{
      k: "Audit Risk",
      v: "Minimal"
    }, {
      k: "Compliance",
      v: "Full"
    }, {
      k: "Turnaround",
      v: "3 days"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/corporate_tax_filing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/corporate_tax_filing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/corporate_tax_filing.png-wvc-sizes"
  }, {
    label: "ADVISORY — PROACTIVE",
    name: "Real-Time Financial Insights",
    description: "A premier advisory platform with regulatory intelligence and proactive quantification. Captures cash flow mechanics and tax dynamics in real time, with the nuance of a fine financial instrument.",
    specs: [{
      k: "Resolution",
      v: "High"
    }, {
      k: "Update Rate",
      v: "Monthly"
    }, {
      k: "Dimensions",
      v: "360°"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/financial_advisory_dashboard.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/financial_advisory_dashboard.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/financial_advisory_dashboard.webp-wvc-sizes"
  }, {
    label: "RISK — CONTINUOUS",
    name: "Audit Risk Management",
    description: "Discreet, ongoing monitoring that records every transaction across the fiscal year. Reveals the silent discrepancies and tax subtleties that a standard audit alone cannot disclose.",
    specs: [{
      k: "Duration",
      v: "365 days"
    }, {
      k: "Channels",
      v: "Multi-Source"
    }, {
      k: "Sampling",
      v: "100%"
    }],
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/audit_risk_monitoring.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/audit_risk_monitoring.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/audit_risk_monitoring.webp-wvc-sizes"
  }];
  return <section data-section-id="3385" id="the-lovejoy-standard" className="bg-background text-foreground relative w-full py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div ref={header.ref} className={`mb-20 md:mb-28 max-w-4xl transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
            The Lovejoy Standard — Our Methodology
          </p>
          <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
            The *architecture* of financial clarity.
          </h2>
          <p className="font-default text-muted-foreground mt-6 max-w-xl text-base md:text-lg leading-relaxed">
            Each service is selected not for novelty, but for the quiet
            accuracy it brings to a careful financial strategy.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {instruments.map((item, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          const offsetClass = i === 0 ? "md:mt-0" : i === 1 ? "md:mt-16" : i === 2 ? "md:-mt-8" : "md:mt-8";
          return <div key={i} data-index={i} ref={reveal.ref} className={`${offsetClass} transition-all duration-[900ms] ease-out`} style={{
            transitionDelay: reveal.isVisible ? `${i * 150}ms` : "0ms",
            opacity: reveal.isVisible ? 1 : 0,
            transform: reveal.isVisible ? "translateY(0)" : "translateY(40px)"
          }}>
                <Card className="group bg-card border-l border-l-muted-foreground p-6 md:p-10 lg:p-12 transition-all duration-500 ease-out hover:bg-accent/10 hover:border-l-4 hover:border-l-accent overflow-hidden">
                  <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-muted">
                    <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
                    <div className="pointer-events-none absolute inset-0 bg-primary/0 transition-colors duration-500 group-hover:bg-primary/10" />
                  </div>

                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground mt-8">
                    {item.label}
                  </p>

                  <h3 className="font-default font-semibold text-2xl md:text-3xl lg:text-[2rem] leading-tight tracking-tight text-foreground mt-3">
                    {item.name}
                  </h3>

                  <p className="font-default text-muted-foreground mt-4 text-base leading-relaxed">
                    {item.description}
                  </p>

                  <div className="mt-8 pt-6 border-t border-border grid grid-cols-3 gap-4">
                    {item.specs.map((spec, j) => <div key={j} data-index={j} className="flex flex-col">
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
                          {spec.k}
                        </span>
                        <span className="font-mono text-lg md:text-xl text-foreground">
                          {spec.v}
                        </span>
                      </div>)}
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}